//npm packages
import React, { useEffect, useCallback, memo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Field, reduxForm, change, formValueSelector } from 'redux-form';
import { Col, Row, Button } from 'reactstrap';
import swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';

//redux actions
import { generateReport, reportForm, REPORTFORM } from '../../redux/actions';

//elements starts with capital letter;
import { InputField, SelectField, FormBase, Validator } from '../../components';

//helper files
import consts from '../../consts';
import strings from '../../globalization';
import utility from '../../utility';
import EquipmentList from '../Equipment/EquipmentList';
import { string } from 'prop-types';
import BasicRowColGrid from '../../utility/BasicRowColGrid';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

const {
  CLOCS,
  CLOCSWE,
  LBR,
  NLOLM,
  WGL,
  LE30,
  LE60,
  LE90,
  LE,
  LWGP,
  LWHS,
  ABPM,
  BCT,
  BTOE,
  BSR,
  LBI,
  BD,
  LWEO,
  LBTS
} = consts.Reports;

const reportFormatOptions = consts.reportFormatOptions;
const Report = memo(props => {
  const { initialValues = {}, comboType = {}, equipmentData = {} } = props,
    { params } = props.match;
  const dispatch = useDispatch(),
    history = useHistory();
  const [state, setState] = useState({
    siteEnable: false,
    connectivityEnable: false,
    regionEnable: false,
    showInput: false
  });
  const [inputInfo, setInputInfo] = useState({});

  const { siteEnable, connectivityEnable, regionEnable, showInput } = state;

  useEffect(() => {
    dispatch(
      reportForm({
        action: 'load',
        _id: 'new',
        comboType: [
          'Reports',
          'ConnectivityType',
          'Region',
          'User',
          'EquipmentModel',
          'EquipmentType',
          'TrackingSystem'
        ]
      })
    );
  }, [window.location.hash]);

  const equipmentOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'Name' },
      comboType.EquipmentType
    ) || [];

  const reportsOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Reports
    ) || [];

  const siteTypeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'Name' },
      comboType.Facility
    ) || [];

  const connectivityOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.ConnectivityType
    ) || [];

  const regionOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Region
    ) || [];

  const equipmentModelOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'ModelName' },
      comboType.EquipmentModel
    ) || [];

  const userOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'UserName' },
      comboType.User
    ) || [];

  const trackingSystemOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.TrackingSystem
    ) || [];

  const onSubmit = useCallback(
    values => {
      let filterValue = values.Value,
        reportId = values.Report.value,
        format = values.Format.value,
        title = values.Report.label;
      if (inputInfo.type === 'select' && values.Value) {
        filterValue = values.Value.label;
      }
      if (reportId === LWEO) {
        title = title + ' ' + dayjs(filterValue).format('MM/DD/YYYY');
      }
      if (ABPM === reportId) {
        filterValue = values.Value.label + ',' + values.EquipmentType.label;
      }
      const url = `${
        consts.hostURL
      }/generateReport?report=${reportId}&format=${format}&title=${title}&filterValue=${encodeURIComponent(
        filterValue
      )}`;
      axios
        .request({
          method: 'post',
          baseURL: consts.hostURL,
          url: url,
          withCredentials: true,
          timeout: consts.defaultValue.requestTimeout
        })
        .then(res => {
          if (res.data) {
            window.open(url, '_blank');
          } else {
            swal.fire(strings.Error, strings.NoRecordsFound, 'error');
          }
        })
        .catch(() => {
          swal.fire(strings.Error, 'There seem to be an issue', 'error');
        });
    },
    [inputInfo]
  );

  const onEquipmentTypeChange = ({ value }) => {
    const models = comboType.EquipmentModel;
    let modelOptions = models.filter(
      option => option.EquipmentTypeId === value
    );
    modelOptions =
      utility.remoteComboLoader(
        { valueProp: '_id', displayValue: 'ModelName' },
        modelOptions
      ) || [];
    setInputInfo({ ...inputInfo, options: modelOptions });
    if (!modelOptions.length) {
      props.change('Value', '');
    }
  };

  const onReportChange = values => {
    let val = values.value;
    let siteEnable = CLOCS === val,
      connectivityEnable = BCT === val,
      regionEnable = LBR === val,
      productModel = ABPM === val,
      isDealer = BD === val,
      isEquipmentType = BTOE === val,
      isIdn = LBI === val,
      isUser = WGL === val,
      isLicenseExpiry = LWEO === val,
      isTrackingSystem = LBTS === val;
    let inputInfo = {};
    if (siteEnable) {
      inputInfo = {
        name: strings.Site,
        options: siteTypeOptions,
        type: 'select'
      };
    } else if (connectivityEnable) {
      inputInfo = {
        name: strings.ConnectivityType,
        options: connectivityOptions,
        type: 'select'
      };
    } else if (regionEnable) {
      inputInfo = {
        name: strings.Region,
        type: 'select',
        options: regionOptions
      };
    } else if (productModel) {
      inputInfo = {
        name: strings.EquipmentModel,
        options: equipmentModelOptions,
        type: 'select'
      };
    } else if (isDealer) {
      inputInfo = {
        name: strings.Dealer,
        type: 'input'
      };
    } else if (isEquipmentType) {
      inputInfo = {
        name: strings.EquipmentType,
        options: equipmentOptions,
        type: 'select'
      };
    } else if (isIdn) {
      inputInfo = {
        name: strings.IDN,
        type: 'input'
      };
    } else if (isUser) {
      inputInfo = {
        name: strings.User,
        type: 'select',
        options: userOptions
      };
    } else if (isLicenseExpiry) {
      inputInfo = {
        name: strings.ExpirationDate,
        type: 'date'
      };
    } else if (isTrackingSystem) {
      inputInfo = {
        name: strings.TrackingSystem,
        type: 'select',
        options: trackingSystemOptions
      };
    }
    setState({
      state,
      showInput:
        regionEnable ||
        connectivityEnable ||
        productModel ||
        isDealer ||
        isUser ||
        isIdn ||
        isEquipmentType ||
        isLicenseExpiry ||
        isTrackingSystem
    });
    props.change('Value', '');
    setInputInfo(inputInfo);
  };

  return (
    <>
      <FormBase
        {...props}
        isDark={props.isDark}
        onSubmit={onSubmit}
        title={strings.ReportDetails}
        actionInfo={{ action: reportForm, actionName: REPORTFORM }}
        form="reportForm"
        disableReset={true}
        historyDisable={true}
        saveText="Generate Report"
        disableCancel={true}
      >
        <>
          <BasicRowColGrid>
            {strings.Report}
            <Field
              name="Report"
              type="select"
              component={SelectField}
              placeholder={strings.Report}
              options={reportsOptions}
              validate={Validator.required}
              onChange={newValue => onReportChange(newValue)}
              menuPortalTarget={document.body}
            />
          </BasicRowColGrid>
          <BasicRowColGrid>
            {strings.Format}
            <Field
              name="Format"
              type="select"
              component={SelectField}
              placeholder={strings.Format}
              options={reportFormatOptions}
              validate={Validator.required}
              menuPortalTarget={document.body}
            />
          </BasicRowColGrid>
          {inputInfo.name === strings.EquipmentModel ? (
            <BasicRowColGrid>
              {strings.EquipmentType}
              <Field
                name="EquipmentType"
                type="select"
                component={SelectField}
                placeholder={strings.EquipmentType}
                validate={Validator.required}
                options={equipmentOptions}
                menuPortalTarget={document.body}
                onChange={onEquipmentTypeChange}
              />
            </BasicRowColGrid>
          ) : null}
          {showInput && (
            <BasicRowColGrid>
              {inputInfo.name}
              <Field
                name="Value"
                type={inputInfo.type}
                component={
                  inputInfo.type === 'input' || inputInfo.type === 'date'
                    ? InputField
                    : inputInfo.type === 'select'
                    ? SelectField
                    : null
                }
                placeholder={inputInfo.name}
                validate={Validator.required}
                options={inputInfo.options}
                menuPortalTarget={document.body}
              />
            </BasicRowColGrid>
          )}
        </>
      </FormBase>
    </>
  );
});

const ReportRedux = reduxForm({
  form: 'reportForm',
  enableReinitialize: true
})(Report);

const mapDispatchToProps = dispatch => ({
  change: (field, value) => dispatch(change('reportForm', field, value))
});

const selector = formValueSelector('reportForm');

const mapStateToProps = state => {
  const { reportForm } = state.form;
  const { data, comboType, success, info } = state.reportFormData.data;
  const { isDark } = state.settingData;

  const reportType = selector(state, 'ReportType');
  return {
    initialValues: data,
    comboType: comboType,
    isDark: isDark,
    formValues: reportForm ? reportForm.values : null,
    shouldGoBack:
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully'),
    loginUserData: state.loginData.data,
    reportType
  };
};

const ReportModule = connect(mapStateToProps, mapDispatchToProps)(ReportRedux);

export default props => {
  return <ReportModule {...props} />;
};
